import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabase.ts';
import { Tenant } from '../types/tenant';

export const useTenants = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tenantsWithDeboarding, setTenantsWithDeboarding] = useState<Record<string, boolean>>({});

  const fetchTenants = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('tenants')
        .select(`
          *,
          property:properties (
            id,
            name,
            address
          ),
          bills:bills(
            id,
            amount,
            status
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTenants(data || []);

      // Fix deboarding eligibility check
      const tenantsWithDeboard = (data || []).reduce((acc: Record<string, boolean>, tenant) => {
        // Check if tenant has unpaid bills
        const hasUnpaidBills = tenant.bills?.some(
          (bill: any) => bill.status === 'Unpaid' || bill.status === 'Partial'
        );

        // Check if lease is ending within 30 days
        const endDate = tenant.end_date ? new Date(tenant.end_date) : null;
        const today = new Date();
        const daysUntilEnd = endDate
          ? Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 3600 * 24))
          : null;
        const isLeaseEnding = daysUntilEnd !== null && daysUntilEnd <= 30 && daysUntilEnd > 0;

        // Only allow deboarding if tenant has unpaid bills OR lease is ending
        acc[tenant.id] = tenant.status === 'Active' && (hasUnpaidBills || isLeaseEnding);
        return acc;
      }, {});

      setTenantsWithDeboarding(tenantsWithDeboard);

    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const addTenant = async (tenantData: Tenant) => {
    try {
      const { data, error } = await supabase
        .from('tenants')
        .insert([tenantData])
        .select()
        .single();

      if (error) throw error;
      setTenants([data, ...tenants]);
      return data;
    } catch (err) {
      throw err;
    }
  };

  const updateTenant = async (id: string, tenantData: Tenant) => {
    try {
      const { data, error } = await supabase
        .from('tenants')
        .update(tenantData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      setTenants(tenants.map(t => t.id === id ? data : t));
      return data;
    } catch (err) {
      throw err;
    }
  };

  const deleteTenant = async (id: string) => {
    try {
      const { error } = await supabase
        .from('tenants')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setTenants(tenants.filter(t => t.id !== id));
    } catch (err) {
      throw err;
    }
  };

  const deboardTenant = async (settlementData: any) => {
    try {
      // Insert settlement record
      const { error: settlementError } = await supabase
        .from('tenant_settlements')
        .insert([settlementData]);

      if (settlementError) throw settlementError;

      // Update tenant status
      const { error: tenantError } = await supabase
        .from('tenants')
        .update({
          status: 'Inactive',
          deboarding_date: new Date().toISOString().split('T')[0]
        })
        .eq('id', settlementData.tenant_id);

      if (tenantError) throw tenantError;

      // Update property status
      const { error: propertyError } = await supabase
        .from('properties')
        .update({ status: 'Vacant' })
        .eq('id', settlementData.property_id);

      if (propertyError) throw propertyError;

      // Refetch tenants to update the UI
      await fetchTenants();

    } catch (error: any) {
      throw new Error(error.message);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  return {
    tenants,
    loading,
    error,
    tenantsWithDeboarding,
    addTenant,
    updateTenant,
    deleteTenant,
    deboardTenant,
    fetchTenants  // Export this function
  };
};
